import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../api/client";
import toast from "react-hot-toast";
import { CogIcon } from "@heroicons/react/24/outline";
import type { AxiosError } from "axios";

interface ApiErrorResponse {
  message?: string;
}

interface SystemSettings {
  _id?: string;
  kpi_report_recipients: string[];
  payout_report_recipients: string[];
  enable_daily_kpi: boolean;
  enable_weekly_kpi: boolean;
  enable_daily_payout_reminder: boolean;
  daily_schedule: string;
  weekly_schedule: string;
  auto_payout_enabled: boolean;
  auto_payout_max_amount: number;
  createdAt?: Date;
  updatedAt?: Date;
}

export default function Settings() {
  const queryClient = useQueryClient();
  const [recipientsInput, setRecipientsInput] = useState("");
  const [payoutRecipientsInput, setPayoutRecipientsInput] = useState("");
  const [autoPayoutMaxAmount, setAutoPayoutMaxAmount] = useState(20);

  const { data, isLoading, error } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => {
      const res = await api.get("/admin/settings");
      return res.data.data as SystemSettings;
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (settings: Partial<SystemSettings>) => {
      const res = await api.put("/admin/settings", settings);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["settings"] });
      toast.success("Settings updated successfully");
    },
    onError: (error: AxiosError<ApiErrorResponse>) => {
      toast.error((error as AxiosError<ApiErrorResponse>).response?.data?.message || "Failed to update settings");
    },
  });

  const sendNowMutation = useMutation({
    mutationFn: async (period: 'daily' | 'weekly') => {
      const res = await api.post("/admin/kpi/send-now", { period });
      return res.data;
    },
    onSuccess: (data, period) => {
      toast.success(`${period.charAt(0).toUpperCase() + period.slice(1)} KPI report sent to ${data.recipients.length} recipient(s)`);
    },
    onError: (error: AxiosError<ApiErrorResponse>) => {
      toast.error((error as AxiosError<ApiErrorResponse>).response?.data?.message || "Failed to send KPI report");
    },
  });

  const sendPayoutReminderMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post("/admin/payout-reminder/send-now");
      return res.data;
    },
    onSuccess: (data) => {
      toast.success(`Payout reminder sent to ${data.recipients.length} recipient(s)`);
    },
    onError: (error: AxiosError<ApiErrorResponse>) => {
      toast.error((error as AxiosError<ApiErrorResponse>).response?.data?.message || "Failed to send payout reminder");
    },
  });

  // Initialize recipients input only once when data loads
  useEffect(() => {
    if (data) {
      setRecipientsInput(data.kpi_report_recipients.join(", "));
      setPayoutRecipientsInput((data.payout_report_recipients || []).join(", "));
      setAutoPayoutMaxAmount(data.auto_payout_max_amount ?? 20);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount - data is stable from react-query

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    // Parse KPI recipients
    const kpiRecipients = recipientsInput
      .split(",")
      .map(email => email.trim())
      .filter(email => email.length > 0);

    // Parse payout recipients
    const payoutRecipients = payoutRecipientsInput
      .split(",")
      .map(email => email.trim())
      .filter(email => email.length > 0);

    // Validate all emails
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    for (const email of [...kpiRecipients, ...payoutRecipients]) {
      if (!emailRegex.test(email)) {
        toast.error(`Invalid email format: ${email}`);
        return;
      }
    }

    const settings: Partial<SystemSettings> = {
      kpi_report_recipients: kpiRecipients,
      payout_report_recipients: payoutRecipients,
      enable_daily_kpi: formData.get("enable_daily_kpi") === "on",
      enable_weekly_kpi: formData.get("enable_weekly_kpi") === "on",
      enable_daily_payout_reminder: formData.get("enable_daily_payout_reminder") === "on",
      daily_schedule: formData.get("daily_schedule") as string,
      weekly_schedule: formData.get("weekly_schedule") as string,
      auto_payout_enabled: formData.get("auto_payout_enabled") === "on",
      auto_payout_max_amount: autoPayoutMaxAmount,
    };

    updateMutation.mutate(settings);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 dark:text-gray-400">Loading settings...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">Error loading settings</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3">
        <CogIcon className="h-8 w-8 text-gray-700 dark:text-gray-300" />
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">System Settings</h1>
      </div>

      <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white mb-4">
            KPI Report Configuration
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            Configure automated KPI report settings. Changes will be applied on the next scheduled job run.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Recipients */}
            <div>
              <label htmlFor="recipients" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Report Recipients
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                Enter email addresses separated by commas
              </p>
              <input
                type="text"
                name="recipients"
                id="recipients"
                value={recipientsInput}
                onChange={(e) => setRecipientsInput(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                placeholder="nd@digitaldavid.io, admin@digitaldavid.io"
              />
            </div>

            {/* Daily Report Toggle */}
            <div className="flex items-start">
              <div className="flex h-5 items-center">
                <input
                  id="enable_daily_kpi"
                  name="enable_daily_kpi"
                  type="checkbox"
                  defaultChecked={data?.enable_daily_kpi}
                  className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="enable_daily_kpi" className="font-medium text-gray-700 dark:text-gray-300">
                  Enable Daily KPI Report
                </label>
                <p className="text-gray-500 dark:text-gray-400">
                  Send a daily KPI report according to the schedule below
                </p>
              </div>
            </div>

            {/* Daily Schedule */}
            <div>
              <label htmlFor="daily_schedule" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Daily Report Schedule
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                Cron expression (e.g., "0 9 * * *" for 9 AM daily)
              </p>
              <input
                type="text"
                name="daily_schedule"
                id="daily_schedule"
                defaultValue={data?.daily_schedule || "0 9 * * *"}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                placeholder="0 9 * * *"
              />
            </div>

            {/* Weekly Report Toggle */}
            <div className="flex items-start">
              <div className="flex h-5 items-center">
                <input
                  id="enable_weekly_kpi"
                  name="enable_weekly_kpi"
                  type="checkbox"
                  defaultChecked={data?.enable_weekly_kpi}
                  className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="enable_weekly_kpi" className="font-medium text-gray-700 dark:text-gray-300">
                  Enable Weekly KPI Report
                </label>
                <p className="text-gray-500 dark:text-gray-400">
                  Send a weekly KPI report according to the schedule below
                </p>
              </div>
            </div>

            {/* Weekly Schedule */}
            <div>
              <label htmlFor="weekly_schedule" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Weekly Report Schedule
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                Cron expression (e.g., "0 9 * * 1" for 9 AM Mondays)
              </p>
              <input
                type="text"
                name="weekly_schedule"
                id="weekly_schedule"
                defaultValue={data?.weekly_schedule || "0 9 * * 1"}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                placeholder="0 9 * * 1"
              />
            </div>

            {/* Payout Reminder Recipients */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mt-6">
              <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                Payout Reminder Configuration
              </h4>

              <div className="mb-4">
                <label htmlFor="payout_recipients" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Payout Reminder Recipients
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Enter email addresses separated by commas (receives daily payout status)
                </p>
                <input
                  type="text"
                  name="payout_recipients"
                  id="payout_recipients"
                  value={payoutRecipientsInput}
                  onChange={(e) => setPayoutRecipientsInput(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder="finance@digitaldavid.io"
                />
              </div>

              <div className="flex items-start">
                <div className="flex h-5 items-center">
                  <input
                    id="enable_daily_payout_reminder"
                    name="enable_daily_payout_reminder"
                    type="checkbox"
                    defaultChecked={data?.enable_daily_payout_reminder}
                    className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="enable_daily_payout_reminder" className="font-medium text-gray-700 dark:text-gray-300">
                    Enable Daily Payout Reminder
                  </label>
                  <p className="text-gray-500 dark:text-gray-400">
                    Sends daily at 10 AM with scheduled payout count and critical overdue alerts
                  </p>
                </div>
              </div>
            </div>

            {/* Auto-Payout Configuration */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mt-6">
              <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                Auto-Payout Configuration
              </h4>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Automatically schedule payouts for low-risk, low-value claims when contract is completed.
              </p>

              <div className="flex items-start mb-4">
                <div className="flex h-5 items-center">
                  <input
                    id="auto_payout_enabled"
                    name="auto_payout_enabled"
                    type="checkbox"
                    defaultChecked={data?.auto_payout_enabled}
                    className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="auto_payout_enabled" className="font-medium text-gray-700 dark:text-gray-300">
                    Enable Auto-Payout
                  </label>
                  <p className="text-gray-500 dark:text-gray-400">
                    Automatically schedule payout when risk score is 0 (clean) and amount is below threshold
                  </p>
                </div>
              </div>

              <div>
                <label htmlFor="auto_payout_max_amount" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Maximum Auto-Payout Amount (EUR)
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Only auto-payout if the purchase price is at or below this amount
                </p>
                <input
                  type="number"
                  name="auto_payout_max_amount"
                  id="auto_payout_max_amount"
                  min="0"
                  step="1"
                  value={autoPayoutMaxAmount}
                  onChange={(e) => setAutoPayoutMaxAmount(Number(e.target.value))}
                  className="mt-1 block w-32 rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={updateMutation.isPending}
                className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-indigo-500 dark:hover:bg-indigo-600"
              >
                {updateMutation.isPending ? "Saving..." : "Save Settings"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Info Card */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3 flex-1">
            <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
              About Cron Expressions
            </h3>
            <div className="mt-2 text-sm text-blue-700 dark:text-blue-300">
              <p>Cron format: minute hour day month weekday</p>
              <p className="mt-1">Examples:</p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>"0 9 * * *" - Every day at 9:00 AM</li>
                <li>"0 9 * * 1" - Every Monday at 9:00 AM</li>
                <li>"0 17 * * 5" - Every Friday at 5:00 PM</li>
                <li>"0 8 1 * *" - First day of every month at 8:00 AM</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Test Buttons */}
      {data && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white mb-4">
              Test KPI Reports
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Send a KPI report immediately to test the configuration
            </p>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => sendNowMutation.mutate('daily')}
                disabled={sendNowMutation.isPending || !data.enable_daily_kpi || data.kpi_report_recipients.length === 0}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-green-500 dark:hover:bg-green-600"
              >
                {sendNowMutation.isPending ? "Sending..." : "Send Daily Report Now"}
              </button>
              <button
                type="button"
                onClick={() => sendNowMutation.mutate('weekly')}
                disabled={sendNowMutation.isPending || !data.enable_weekly_kpi || data.kpi_report_recipients.length === 0}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-blue-500 dark:hover:bg-blue-600"
              >
                {sendNowMutation.isPending ? "Sending..." : "Send Weekly Report Now"}
              </button>
            </div>
            {data.kpi_report_recipients.length === 0 && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-2">
                Note: Add at least one recipient to test reports.
              </p>
            )}
            {(!data.enable_daily_kpi || !data.enable_weekly_kpi) && data.kpi_report_recipients.length > 0 && (
              <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-2">
                Note: Disabled reports cannot be sent. Enable them above to test.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Test Payout Reminder */}
      {data && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white mb-4">
              Test Payout Reminder
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Send a payout reminder immediately to test the configuration
            </p>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => sendPayoutReminderMutation.mutate()}
                disabled={sendPayoutReminderMutation.isPending || !data.enable_daily_payout_reminder || (data.payout_report_recipients || []).length === 0}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-orange-500 dark:hover:bg-orange-600"
              >
                {sendPayoutReminderMutation.isPending ? "Sending..." : "Send Payout Reminder Now"}
              </button>
            </div>
            {(data.payout_report_recipients || []).length === 0 && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-2">
                Note: Add at least one payout reminder recipient to test.
              </p>
            )}
            {!data.enable_daily_payout_reminder && (data.payout_report_recipients || []).length > 0 && (
              <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-2">
                Note: Payout reminder is disabled. Enable it above to test.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Current Settings Display */}
      {data && (
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Current Configuration</h4>
          <dl className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2 text-sm">
            <div>
              <dt className="text-gray-500 dark:text-gray-400">Last Updated</dt>
              <dd className="text-gray-900 dark:text-white">
                {data.updatedAt ? new Date(data.updatedAt).toLocaleString() : "Never"}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500 dark:text-gray-400">Recipients Count</dt>
              <dd className="text-gray-900 dark:text-white">
                {data.kpi_report_recipients.length} email(s)
              </dd>
            </div>
            <div>
              <dt className="text-gray-500 dark:text-gray-400">Daily Report Status</dt>
              <dd className={data.enable_daily_kpi ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}>
                {data.enable_daily_kpi ? "Enabled" : "Disabled"}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500 dark:text-gray-400">Weekly Report Status</dt>
              <dd className={data.enable_weekly_kpi ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}>
                {data.enable_weekly_kpi ? "Enabled" : "Disabled"}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500 dark:text-gray-400">Auto-Payout Status</dt>
              <dd className={data.auto_payout_enabled ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}>
                {data.auto_payout_enabled ? `Enabled (max ${data.auto_payout_max_amount ?? 20} EUR)` : "Disabled"}
              </dd>
            </div>
          </dl>
        </div>
      )}
    </div>
  );
}
